import React, { useEffect, useRef, useState } from 'react';
import { X, ShieldCheck, Scale } from 'lucide-react';
import { useModalAccessibility } from '../hooks/useModalAccessibility';
import { LEGAL_IDENTITY } from '../legalIdentity';

interface PrivacyModalProps {
  onClose: () => void;
  onAccept: () => void;
}

export const PrivacyModal: React.FC<PrivacyModalProps> = ({ onClose, onAccept }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [publication, setPublication] = useState<{ terms: { text: string }; privacy: { text: string } } | null>(null);
  const [legalState, setLegalState] = useState<'loading' | 'ready' | 'failed'>('loading');
  useEffect(() => {
    const controller = new AbortController();
    void fetch('/api/legal/current', { signal: controller.signal }).then(response => response.ok ? response.json() : null)
      .then(value => {
        if (controller.signal.aborted) return;
        if (value?.mode === 'economic' && typeof value.terms?.text === 'string' && typeof value.privacy?.text === 'string') {
          setPublication(value); setLegalState('ready');
        } else setLegalState(value?.mode === 'legacy' ? 'ready' : 'failed');
      })
      .catch(() => { if (!controller.signal.aborted) setLegalState('failed'); });
    return () => controller.abort();
  }, []);

  useModalAccessibility(dialogRef, { onClose });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Privacidad y terminos"
        tabIndex={-1}
        className="bg-panel rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85dvh] flex flex-col overflow-hidden"
      >

        {/* Header Modal */}
        <div className="p-4 border-b border-line flex justify-between items-center bg-juris-soft">
          <div className="flex items-center gap-2 text-ink font-bold">
            <Scale size={20} />
            <h2 className="text-lg">Privacidad y Términos</h2>
          </div>
          <button onClick={onClose} className="w-11 h-11 flex items-center justify-center hover:bg-juris-soft rounded-full text-muted transition-colors" aria-label="Cerrar privacidad y terminos">
            <X size={20} />
          </button>
        </div>

        {/* Contenido Scrollable */}
        <div className="p-6 overflow-y-auto text-sm text-ink space-y-6 leading-relaxed custom-scrollbar">

          {/* POLÍTICA DE PRIVACIDAD */}
          <section>
            <h3 className="text-lg font-bold text-ink mb-1 flex items-center gap-2">
              <ShieldCheck size={18} /> POLÍTICA DE PRIVACIDAD DE JURIS
            </h3>
            <p className="text-xs text-muted mb-4">Fecha de última actualización: 03/03/2026</p>

            <div className="space-y-4 pl-1">

              <div>
                <strong className="text-ink block">1. Responsable del tratamiento</strong>
                <ul className="list-disc pl-5 mt-1 text-muted space-y-1">
                  <li>Nombre comercial: {LEGAL_IDENTITY.commercialName}</li>
                  <li>Email de contacto (privacidad y soporte): <a href={`mailto:${LEGAL_IDENTITY.email}`} className="underline">{LEGAL_IDENTITY.email}</a></li>
                  <li>Sitio web: juris.es</li>
                  <li>App/SaaS: chat.juris.es</li>
                </ul>
              </div>

              <div>
                <strong className="text-ink block">2. Datos personales que se recogen</strong>
                <p className="mt-1">Según el uso del sitio web y de la app, se tratan las siguientes categorías:</p>

                <div className="mt-2 pl-2 border-l-2 border-line space-y-2">
                  <p><strong>2.1. Datos de cuenta</strong><br />
                    • Email.<br />
                    • Contraseña (solo si te registras con email; se gestiona mediante sistemas de autenticación).<br />
                    • Datos necesarios para el acceso con Google (p. ej., email e identificadores de cuenta) si eliges “Acceder con Google”.</p>

                  <p><strong>2.2. Datos de perfil profesional (si los facilitas)</strong><br />
                    • Cuerpo (FFCCSE), ámbito/especialidad, Comunidad Autónoma, antigüedad y rango de edad.</p>

                  <p><strong>2.3. Contenido aportado en la app</strong><br />
                    • Texto de consultas.<br />
                    • Conversaciones/historial.<br />
                    • Imágenes y audios que el usuario suba para transcripción/análisis o para el uso normal del servicio (se almacenan asociados a su cuenta, conforme a la conservación indicada).</p>

                  <p><strong>2.4. Datos de soporte</strong><br />
                    • ID de usuario y de chat, correo, asunto, descripción y los últimos seis (6) mensajes del chat actual, cuando se envía un reporte desde la pestaña de soporte.</p>

                  <p><strong>2.5. Datos técnicos y de seguridad</strong><br />
                    • Dirección IP, identificadores técnicos de solicitud y traza, fecha/hora, dispositivo/navegador (user agent), registros de errores y eventos técnicos saneados (Sentry), necesarios para el funcionamiento, seguridad y prevención de abusos. Juris no activa grabaciones de sesión en Sentry.</p>

                  <p><strong>2.6. Datos de pago</strong><br />
                    • La contratación y los pagos se realizan mediante Stripe. Juris no almacena datos completos de tarjetas. Stripe puede tratar datos de pago e identificación necesarios para la transacción y la gestión de suscripciones, conforme a su propia política y condiciones.</p>
                </div>

                <div className="mt-3 bg-warning-soft border-l-4 border-warning p-3 rounded-r-md">
                  <strong className="text-warning block mb-1">Aviso importante (minimización y uso correcto)</strong>
                  <p className="text-warning text-xs">
                    La app no está diseñada para tratar datos personales reales de terceras personas. No deben introducirse nombres reales, DNIs/NIEs, direcciones, matrículas, teléfonos ni datos de víctimas/detenidos/testigos. Usa supuestos ficticios o datos anonimizados.
                  </p>
                </div>
              </div>

              <div>
                <strong className="text-ink block">3. Finalidades del tratamiento</strong>
                <p className="mt-1">Los datos se tratan para:</p>
                <ol className="list-decimal pl-5 mt-1 text-muted space-y-1">
                  <li>Alta, gestión y acceso a la cuenta (registro, autenticación y mantenimiento de sesión).</li>
                  <li>Prestación del servicio (generación de respuestas, gestión del historial y funciones de transcripción/análisis).</li>
                  <li>Gestión de la suscripción mensual (control de acceso por plan, renovaciones, cancelaciones y estado de pago).</li>
                  <li>Soporte (gestión de incidencias y mejoras).</li>
                  <li>Seguridad y prevención de abuso/fraude (protección del servicio, investigación de incidentes, control técnico).</li>
                  <li>Analítica web (GA4) para medir el uso del sitio y mejorar su rendimiento, conforme a esta política.</li>
                  <li>Cumplimiento legal (obligaciones fiscales/contables cuando proceda, y atención de requerimientos válidos de autoridades).</li>
                </ol>
              </div>

              <div>
                <strong className="text-ink block">4. Base jurídica del tratamiento</strong>
                <ul className="list-disc pl-5 mt-1 text-muted space-y-1">
                  <li>Ejecución de contrato (art. 6.1.b RGPD): cuenta, prestación del SaaS, soporte asociado y gestión de suscripción.</li>
                  <li>Consentimiento (art. 6.1.a RGPD): aceptación de esta política, analítica (GA4) y cualquier tratamiento opcional que lo requiera.</li>
                  <li>Obligación legal (art. 6.1.c RGPD): cumplimiento de deberes legales aplicables.</li>
                  <li>Interés legítimo (art. 6.1.f RGPD): seguridad, prevención de abuso/fraude, mantenimiento y defensa ante reclamaciones.</li>
                </ul>
              </div>

              <div>
                <strong className="text-ink block">5. Conservación de los datos</strong>
                <ul className="list-disc pl-5 mt-1 text-muted space-y-1">
                  <li>Cuenta y perfil: mientras la cuenta esté activa.</li>
                  <li>Conversaciones, imágenes y audios: se conservan hasta que el usuario elimine su cuenta o solicite el borrado.</li>
                  <li>Soporte: hasta 12 meses desde el cierre del ticket/comunicación, salvo eliminación previa de la cuenta o solicitud de borrado aplicable.</li>
                  <li>Logs técnicos y de seguridad: 180 días.</li>
                  <li>Copias de seguridad: 90 días (ciclo de backups), tras lo cual se sobrescriben/eliminan.</li>
                </ul>
                <p className="mt-1 text-muted">Si existe una obligación legal de conservación o necesidad para la formulación/defensa de reclamaciones, se conservarán los datos estrictamente necesarios durante los plazos legalmente exigibles.</p>
              </div>

              <div>
                <strong className="text-ink block">6. Destinatarios, encargados del tratamiento y servicios de terceros</strong>
                <p className="mt-1">Para prestar el servicio, pueden acceder/tratar datos proveedores tecnológicos indispensables:</p>
                <ul className="list-disc pl-5 mt-1 text-muted space-y-1">
                  <li>Google Firebase (UE): autenticación y base de datos/infraestructura del servicio.</li>
                  <li>Google Analytics 4 (GA4): analítica del sitio web conforme a la aceptación de esta política.</li>
                  <li>Stripe: pagos y suscripciones (actúa conforme a su rol y documentación).</li>
                  <li>Vercel (infraestructura serverless para el backend de la API del servicio).</li>
                  <li>Supabase (almacenamiento vectorial, indexación y procesamiento de documentos legislativos para la base de conocimiento del servicio).</li>
                  <li>Resend (envío de notificaciones transaccionales asociadas a reportes de soporte).</li>
                  <li>Sentry (Functional Software, Inc.): servicio de observabilidad, monitoreo de errores y rendimiento para garantizar la estabilidad y mejora técnica del servicio.</li>
                  <li>Google reCAPTCHA: servicio de protección contra fraude y abuso mediante el análisis de interacciones técnicas para diferenciar usuarios humanos de sistemas automatizados (usado para Firebase App Check).</li>
                  <li>Proveedor de IA (por API):
                    <ul className="list-[circle] pl-5 mt-1">
                      <li>OpenAI (generación de respuestas, transcripción/análisis cuando aplique, y búsquedas por Internet para apoyo en consultas de jurisprudencia y normativa cuando se utilice esa función).</li>
                    </ul>
                  </li>
                </ul>
                <p className="mt-1 text-muted">No se realizan ventas de datos a terceros. Solo se comunicarán datos a terceros por obligación legal o requerimiento válido.</p>
              </div>

              <div>
                <strong className="text-ink block">7. Transferencias internacionales</strong>
                <p className="mt-1">Aunque parte de la infraestructura se configura en UE, algunos proveedores pueden operar globalmente y puede existir acceso o tratamiento desde fuera del EEE.<br />
                  Cuando haya transferencias internacionales, se aplicarán las garantías exigidas por el RGPD, como Cláusulas Contractuales Tipo (SCC) u otras medidas legalmente válidas, según corresponda.</p>
              </div>

              <div>
                <strong className="text-ink block">8. Uso de IA y no entrenamiento de modelos</strong>
                <p className="mt-1">Juris utiliza un proveedor de IA por API. El servicio se configura y opera para no utilizar los datos de usuarios para entrenar modelos. Además, se trabaja con condiciones contractuales y configuraciones disponibles para limitar el uso de los datos a la prestación del servicio y a finalidades de seguridad.</p>
              </div>

              <div>
                <strong className="text-ink block">9. Derechos de las personas usuarias</strong>
                <p className="mt-1">Puedes ejercer los derechos de:</p>
                <ul className="list-disc pl-5 mt-1 text-muted">
                  <li>Acceso, rectificación, supresión, oposición, limitación, portabilidad y, cuando proceda, a no ser objeto de decisiones automatizadas.</li>
                </ul>
                <p className="mt-1"><strong>Cómo ejercerlos:</strong> escribe a {LEGAL_IDENTITY.email} indicando:</p>
                <ul className="list-disc pl-5 mt-1 text-muted">
                  <li>el derecho que solicitas,</li>
                  <li>el email de tu cuenta,</li>
                  <li>y la información necesaria para gestionar la petición.</li>
                </ul>
                <p className="mt-1 text-muted">Podrá solicitarse información adicional para verificar la identidad cuando sea necesario.</p>
                <p className="mt-1 text-muted"><strong>Reclamación:</strong> si consideras vulnerados tus derechos, puedes reclamar ante la Agencia Española de Protección de Datos (AEPD).</p>
              </div>

              <div>
                <strong className="text-ink block">10. Cookies</strong>
                <p className="mt-1">En juris.es se usan:</p>
                <ul className="list-disc pl-5 mt-1 text-muted">
                  <li>Cookies técnicas necesarias para el funcionamiento.</li>
                  <li>Analítica web (GA4) conforme a la aceptación de esta política.</li>
                </ul>
                <p className="mt-1">Puedes ejercer tus derechos y solicitar información sobre este tratamiento escribiendo al email de contacto indicado en esta política.</p>
              </div>

              <div>
                <strong className="text-ink block">11. Medidas de seguridad</strong>
                <p className="mt-1">Se aplican medidas técnicas y organizativas razonables, incluyendo:</p>
                <ul className="list-disc pl-5 mt-1 text-muted">
                  <li>cifrado de comunicaciones (HTTPS/TLS),</li>
                  <li>control de accesos y mínimos privilegios,</li>
                  <li>registros de actividad y medidas antiabuso,</li>
                  <li>copias de seguridad con ciclo de retención,</li>
                  <li>procedimientos de gestión de incidencias.</li>
                </ul>
              </div>

              <div>
                <strong className="text-ink block">12. Cambios en esta política</strong>
                <p className="mt-1">La política puede actualizarse por cambios legales, técnicos o del servicio. La versión vigente estará publicada con su fecha. Si el cambio es relevante, se notificará por medios razonables (por ejemplo, dentro de la app o por email si procede).</p>
              </div>

            </div>
          </section>

          <hr className="border-line" />

          {/* TÉRMINOS Y CONDICIONES */}
          {publication ? <>
            <p className="text-xs text-muted">Vista pública con identificativos omitidos. La oferta y la confirmación de cada contratación conservan sus textos íntegros y pueden descargarse desde la cuenta.</p>
            <section className="whitespace-pre-wrap"><h3 className="text-lg font-bold text-ink mb-1 flex items-center gap-2"><ShieldCheck size={18} /> PRIVACIDAD DEL SISTEMA ECONÓMICO</h3>{publication.privacy.text}</section>
            <section className="whitespace-pre-wrap"><h3 className="text-lg font-bold text-ink mb-1 flex items-center gap-2"><Scale size={18} /> TÉRMINOS Y CONDICIONES DE USO DE JURIS</h3>{publication.terms.text}</section>
          </> : <section>
            <h3 className="text-lg font-bold text-ink mb-1 flex items-center gap-2">
              <Scale size={18} /> TÉRMINOS Y CONDICIONES DE USO DE JURIS
            </h3>
            <p className="text-xs text-muted mb-4">Fecha de última actualización: 03/03/2026</p>

            <div className="space-y-4 pl-1">

              <div>
                <strong className="text-ink block">1. Identificación del titular (LSSI)</strong>
                <ul className="list-disc pl-5 mt-1 text-muted space-y-1">
                  <li>Nombre comercial: {LEGAL_IDENTITY.commercialName}</li>
                  <li>Email de contacto: <a href={`mailto:${LEGAL_IDENTITY.email}`} className="underline">{LEGAL_IDENTITY.email}</a></li>
                  <li>Web: juris.es</li>
                  <li>App/SaaS: chat.juris.es</li>
                </ul>
              </div>

              <div>
                <strong className="text-ink block">2. Objeto y ámbito de aplicación</strong>
                <p className="mt-1">Estos términos regulan el acceso y uso de:</p>
                <ul className="list-disc pl-5 mt-1 text-muted">
                  <li>el sitio web juris.es, y</li>
                  <li>la aplicación/servicio SaaS Juris accesible en chat.juris.es (la “App”).</li>
                </ul>
                <p className="mt-1">Juris es un servicio orientado principalmente a profesionales de las Fuerzas y Cuerpos de Seguridad en España, y también puede ser utilizado por estudiantes con fines formativos.</p>
              </div>

              <div>
                <strong className="text-ink block">3. Acceso, registro y requisitos de edad</strong>
                <p className="mt-1">3.1. Para usar la App es necesario registrarse mediante email y contraseña o mediante Acceso con Google.</p>
                <p className="mt-1">3.2. El usuario debe tener 18 años o más para crear una cuenta.</p>
                <p className="mt-1">3.3. El usuario es responsable de mantener la confidencialidad de sus credenciales y de todo uso realizado desde su cuenta.</p>
                <p className="mt-1">3.4. Cualquier acceso no autorizado o incidente debe comunicarse de inmediato a {LEGAL_IDENTITY.email}.</p>
              </div>

              <div>
                <strong className="text-ink block">4. Reglas de uso y obligaciones del usuario</strong>
                <p className="mt-1">El usuario se compromete a:</p>
                <ul className="list-disc pl-5 mt-1 text-muted space-y-1">
                  <li>usar Juris de forma lícita y conforme a estos términos;</li>
                  <li>no vulnerar la seguridad del servicio, ni interferir en su disponibilidad;</li>
                  <li>no realizar ingeniería inversa, scraping, extracción masiva de contenido ni reventa del servicio;</li>
                  <li>no introducir contenido ilícito o que vulnere derechos de terceros.</li>
                </ul>

                <div className="mt-3 bg-danger-soft border-l-4 border-danger p-3 rounded-r-md">
                  <strong className="text-danger block mb-1">4.1. Prohibición absoluta de introducir datos personales reales de terceros (CRÍTICO)</strong>
                  <p className="text-danger text-xs">
                    Queda terminantemente prohibido introducir en consultas o archivos cualquier dato personal real de terceras personas, incluyendo, sin limitarse a:
                    <br />• nombre y apellidos, DNI/NIE, direcciones, teléfonos, matrículas,
                    <br />• datos de víctimas/detenidos/testigos,
                    <br />• categorías especiales de datos (salud, ideología, etc.) referidas a personas identificadas o identificables.
                    <br /><br />
                    El usuario debe utilizar supuestos ficticios o datos anonimizados.
                    <br />
                    El incumplimiento de esta regla es responsabilidad exclusiva del usuario y podrá dar lugar a suspensión o cancelación de la cuenta (ver cláusula 11).
                  </p>
                </div>
              </div>

              <div>
                <strong className="text-ink block">5. Naturaleza del servicio y limitación de garantías</strong>
                <p className="mt-1">5.1. Juris proporciona respuestas generadas por IA con finalidad informativa y de apoyo.</p>
                <p className="mt-1">5.2. No sustituye el criterio profesional del usuario, la cadena de mando, ni el asesoramiento jurídico oficial.</p>
                <p className="mt-1">5.3. La IA puede cometer errores, omisiones o desactualizaciones. El usuario debe verificar la información antes de actuar y asumir la responsabilidad de sus decisiones.</p>
                <p className="mt-1">5.4. Salvo dolo o negligencia grave, el titular no responde de daños derivados de:</p>
                <ul className="list-disc pl-5 mt-1 text-muted">
                  <li>uso indebido del servicio,</li>
                  <li>decisiones tomadas exclusivamente por el usuario basadas en el contenido generado,</li>
                  <li>introducción de datos prohibidos o ilícitos por el usuario.</li>
                </ul>
              </div>

              <div>
                <strong className="text-ink block">6. Disponibilidad y mantenimiento</strong>
                <p className="mt-1">El servicio puede experimentar interrupciones por:</p>
                <ul className="list-disc pl-5 mt-1 text-muted">
                  <li>mantenimiento programado,</li>
                  <li>incidencias técnicas,</li>
                  <li>limitaciones o caídas de proveedores de terceros (Firebase, OpenAI, Google, Supabase, Resend, Stripe, Sentry).</li>
                </ul>
                <p className="mt-1">No se garantiza disponibilidad ininterrumpida. Se actuará con diligencia razonable para restaurar el servicio.</p>
              </div>

              <div>
                <strong className="text-ink block">7. Suscripción, pagos y renovación</strong>
                <p className="mt-1">7.1. Juris se ofrece mediante suscripción mensual. Los precios, características del plan y límites aplicables se muestran en la App antes de la contratación.</p>
                <p className="mt-1">7.2. El pago y la gestión de suscripción se realizan a través de la App mediante Stripe, sin intervención humana.</p>
                <p className="mt-1">7.3. La suscripción se renueva automáticamente hasta que el usuario la cancele. El usuario puede cancelar en cualquier momento, manteniendo el acceso hasta el final del periodo ya abonado, salvo que en la App se indique lo contrario para un plan concreto.</p>
              </div>

              <div>
                <strong className="text-ink block">8. Reembolsos</strong>
                <p className="mt-1">Solo se contemplan reembolsos por fallos técnicos atribuibles al servicio que impliquen una interrupción total superior a 24 horas consecutivas.<br />
                  En ese caso, el usuario debe solicitarlo a {LEGAL_IDENTITY.email} indicando:</p>
                <ul className="list-disc pl-5 mt-1 text-muted">
                  <li>email de la cuenta,</li>
                  <li>fecha/hora aproximada del incidente,</li>
                  <li>y una breve descripción.</li>
                </ul>
                <p className="mt-1">No se reembolsan periodos ya disfrutados ni el uso realizado, salvo obligación legal.</p>
              </div>

              <div>
                <strong className="text-ink block">9. Facturación</strong>
                <p className="mt-1">El cobro y los justificantes asociados a la transacción se gestionan mediante Stripe conforme a sus sistemas y condiciones. El usuario es responsable de introducir correctamente sus datos cuando sean requeridos por el proveedor de pagos.</p>
              </div>

              <div>
                <strong className="text-ink block">10. Propiedad intelectual e industrial</strong>
                <p className="mt-1">10.1. La App, el sitio web, el software, diseño, marcas y contenidos propios de Juris están protegidos por la normativa de propiedad intelectual e industrial.</p>
                <p className="mt-1">10.2. Se concede al usuario una licencia limitada, no exclusiva, revocable e intransferible para usar el servicio conforme a estos términos.</p>
                <p className="mt-1">10.3. Queda prohibida la reproducción, distribución, comunicación pública, transformación, ingeniería inversa o explotación del servicio sin autorización expresa.</p>
              </div>

              <div>
                <strong className="text-ink block">11. Suspensión y cancelación de cuentas</strong>
                <p className="mt-1">Juris podrá suspender o cancelar el acceso si el usuario:</p>
                <ul className="list-disc pl-5 mt-1 text-muted space-y-1">
                  <li>incumple estos términos, especialmente la prohibición de introducir datos de terceros,</li>
                  <li>realiza uso abusivo, fraudulento o que comprometa la seguridad,</li>
                  <li>intenta vulnerar el sistema o perjudicar la disponibilidad del servicio.</li>
                </ul>
                <p className="mt-1">La suspensión/cancelación podrá ser inmediata cuando sea necesario para proteger el servicio, sin perjuicio de que el usuario pueda solicitar revisión escribiendo a {LEGAL_IDENTITY.email}.</p>
              </div>

              <div>
                <strong className="text-ink block">12. Enlaces a terceros</strong>
                <p className="mt-1">El sitio o la App pueden incluir enlaces a servicios de terceros. Juris no controla ni responde por contenidos, disponibilidad o prácticas de dichos terceros.</p>
              </div>

              <div>
                <strong className="text-ink block">13. Modificaciones</strong>
                <p className="mt-1">Juris podrá modificar estos términos por motivos legales, técnicos u operativos. La versión vigente estará publicada con su fecha. Si los cambios son relevantes, se notificará por medios razonables.</p>
              </div>

              <div>
                <strong className="text-ink block">14. Legislación aplicable y jurisdicción</strong>
                <p className="mt-1">Estos términos se rigen por la ley española. Las controversias se resolverán ante los juzgados y tribunales que correspondan conforme a la normativa aplicable, respetando el fuero imperativo de las personas consumidoras.</p>
              </div>

              <div>
                <strong className="text-ink block">15. Relación con la Política de Privacidad</strong>
                <p className="mt-1">El tratamiento de datos personales se rige por la Política de Privacidad incluida en esta misma página, que el usuario declara conocer y aceptar al registrarse y utilizar el servicio.</p>
              </div>

            </div>
          </section>}
        </div>

        {/* Footer Modal */}
        <div className="p-4 border-t border-line bg-canvas">
          {legalState === 'failed' && <p role="alert" className="text-sm text-muted mb-3">No se pudieron cargar las condiciones vigentes. Cierra y vuelve a abrir este panel para reintentarlo.</p>}
          <button
            disabled={legalState !== 'ready'}
            onClick={() => {
              if (legalState !== 'ready') return;
              onAccept();
              onClose();
            }}
            className="w-full bg-action text-white py-3 rounded-xl font-bold hover:bg-action-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-black transform active:scale-[0.98]"
          >
            He leído y entendido la Política de privacidad y Términos y condiciones
          </button>
        </div>
      </div>
    </div>
  );
};
